import { DND_RESIZABLE_DEFAULTS, type OnChangeProps } from '@shared/ui';

export type WindowPosition = Pick<OnChangeProps, 'top' | 'left' | 'width' | 'height'>;

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const normalizeWindowPosition = (position: WindowPosition): WindowPosition => {
  const { minWidth, minHeight } = DND_RESIZABLE_DEFAULTS;

  const width = clamp(position.width, minWidth, Math.max(window.innerWidth, minWidth));
  const height = clamp(
    position.height,
    minHeight,
    Math.max(window.innerHeight, minHeight)
  );

  return {
    width,
    height,
    left: clamp(position.left, 0, Math.max(window.innerWidth - width, 0)),
    top: clamp(position.top, 0, Math.max(window.innerHeight - height, 0)),
  };
};

export const readWindowPosition = (
  storageKey: string,
  fallback: WindowPosition
): WindowPosition => {
  try {
    const raw = localStorage.getItem(storageKey);

    if (!raw) return fallback;

    const { top, left, width, height }: Partial<WindowPosition> = JSON.parse(raw);

    if (!isNumber(top) || !isNumber(left) || !isNumber(width) || !isNumber(height)) {
      return fallback;
    }

    return normalizeWindowPosition({ top, left, width, height });
  } catch {
    return fallback;
  }
};

export const writeWindowPosition = (storageKey: string, position: WindowPosition) => {
  const { top, left, width, height } = position;

  localStorage.setItem(storageKey, JSON.stringify({ top, left, width, height }));
};
